
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { OpportunityType } from "@/lib/validations/schema";
import { ColumnDef } from "@tanstack/react-table";
import { AxiosRequestConfig } from "axios";
import { channel_options, close_reason_options } from "common/filters";
import { Img, Spinner } from "components";
import { Option } from "components/Form/form";
import { DataTableColumnHeader } from "components/Table/data-table-column-header";
import { DataTable } from "components/Table/DataTable";
import { useDebounce } from "pages/CreateDeal/newdeal";
import { ChannelOptions } from "pages/Leads/lead";
import { useEffect, useMemo, useState } from "react";
import {
  getFilteredLeads,
  GetFilteredLeadsReqType,
} from "service/apis/Opportunities";
import { toast, Toaster } from "sonner";
import {
  channel_options_inbound,
  channel_options_outbound,
  getRelativeDate,
} from "./utils/featureRule";
import { RowDetailsSheet, stageTags } from "./opportunitydetails";
import { FilterConfig } from "hooks/useGenericFilter";
import GenericFilter from "components/Filters/GenericFilter";

const searchOptions = {
  corporate: {
    columns: ["companyName", "id", "product"],
    placeholder: "Search by ID or Company Name",
  },
  retail: {
    columns: ["name", "id", "product"],
    placeholder: "Search by ID or Name",
  },
  partner: {
    columns: ["id", "name", "product"],
    placeholder: "Search by ID or Entity Name",
  },
};

const tabData = [
  { label: "Retail", value: "retail", enabled: true },
  { label: "Corporate", value: "corporate", enabled: false },
  { label: "Partner", value: "partner", enabled: false },
];

const assignedTypeOptions = [
  { label: "Assigned to me", value: "assignedToMe", enabled: true },
  { label: "Created by me", value: "createdByMe", enabled: false },
  { label: "All leads", value: "all", enabled: false },
];

const remitProductPurposeOptions: Option[] = [
  { value: "17", label: "Education Payment" },
  { value: "18", label: "Study Maintenance" },
  { value: "19", label: "Family Maintenance" },
  { value: "20", label: "Gift" },
];
const visaProductPurposeOptions: Option[] = [{ value: "21", label: "Visa" }];
const travelProductPurposeOptions: Option[] = [
  { value: "12", label: "Flight" },
  { value: "13", label: "Hotel" },
  { value: "14", label: "Travel Package" },
  { value: "15", label: "Travel Insurance" },
  { value: "25", label: "Passport" },
  { value: "26", label: "Dummy Ticket" },
  { value: "27", label: "Dummy Hotels" },
  { value: "28", label: "Ok to board" },
  { value: "29", label: "Baggage" },
];
const forexProductPurposeOptions: Option[] = [
  { value: "16", label: "Prepaid card Thomas Cook" },
  { value: "9", label: "Forex Currency Notes" },
];

const channelOptions = {
  "I Contacted": channel_options_inbound
    .map((option) => ({ label: option.label, value: option.value.toString() }))
    .filter((option) => option.label !== "Meeting"),
  "Customer Contacted": channel_options_outbound.map((option) => {
    if (option.label === "Refferal") {
      return { label: "Referral", value: option.value.toString() };
    }
    return { label: option.label, value: option.value.toString() };
  }),
};

const opportunitiesFilterConfig: FilterConfig = {
  status: {
    label: "By Statussss",
    options: [
      { label: "Open", value: "open" },
      { label: "Buy Later", value: "buyLater" },
      { label: "Not Interested", value: "notInterested" },
      { label: "Spam", value: "spam" },
      { label: "No Response", value: "noResponse" },
      { label: "Success", value: "success" },
      { label: "Not Serviceable", value: "notServicable" },
    ],
  },
  product: {
    label: "By Product",
    options: [], // Will be populated below
    nested: true,
    nestedGroups: {
      remit: remitProductPurposeOptions,
      visa: visaProductPurposeOptions,
      travel: travelProductPurposeOptions,
      forex: forexProductPurposeOptions,
    },
  },
  channel: {
    label: "By Channel",
    options: [], // Will be populated below
    nested: true,
    nestedGroups: {
      "I Contacted": channelOptions["I Contacted"],
      "Customer Contacted": channelOptions["Customer Contacted"],
    },
  },
};

const allcolumns: ColumnDef<OpportunityType>[] = [
  {
    id: "id",
    accessorKey: "id",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        className="whitespace-nowrap"
        title="ID"
      />
    ),
    cell: ({ row }) => {
      const stageTag = stageTags.find(
        (stage) =>
          stage.value.toLowerCase() === row?.original?.stage_tag?.toLowerCase()
      );
      return (
        <div className="flex flex-row gap-2.5 items-start">
          {row?.original?.requestQuote_Type && (
            <div className="relative group mt-[2px]">
              <div className="w-2.5 h-2.5 bg-red-500 rounded-full"></div>
              <span className="absolute bottom-full left-0 mb-1 bg-gray-300 text-[#333] text-xs rounded-md py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                Quotation request for ID: {row.getValue("id")}
              </span>
            </div>
          )}

          <div className="flex flex-col justify-start gap-1.5">
            <p className="capitalize text-sm leading-4">{row.getValue("id")}</p>
            
            {row?.original?.stage_tag && (
              <div className="flex gap-1.5 items-center">
                <span
                  dangerouslySetInnerHTML={{ __html: stageTag?.icon || "" }}
                  className=""
                />
                <h4
                  className={`${stageTag?.color} text-sm leading-4 font-semibold`}
                >
                  {row.original?.stage_tag}
                </h4>
              </div>
            )}
          </div>
        </div>
      );
    },
    enableHiding: false,
    filterFn: (row, id, value) => {
      const rowValue = String(row.getValue(id));
      const searchValue = String(value);
      return rowValue.includes(searchValue);
    },
  },
  {
    id: "companyName",
    accessorKey: "companyName",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Company Name" />
    ),
    cell: ({ row }) => {
      return (
        <div className=" flex gap-10 items-center justify-between">
          {row.getValue("companyName")}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <svg
                  width="17"
                  height="17"
                  viewBox="0 0 17 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.5 1.59375C7.13408 1.59375 5.79882 1.99879 4.6631 2.75766C3.52737 3.51653 2.64218 4.59514 2.11946 5.85709C1.59674 7.11904 1.45998 8.50766 1.72645 9.84734C1.99293 11.187 2.65069 12.4176 3.61655 13.3835C4.5824 14.3493 5.81298 15.0071 7.15266 15.2735C8.49234 15.54 9.88096 15.4033 11.1429 14.8805C12.4049 14.3578 13.4835 13.4726 14.2423 12.3369C15.0012 11.2012 15.4063 9.86593 15.4063 8.5C15.4043 6.66894 14.6761 4.91343 13.3813 3.61868C12.0866 2.32393 10.3311 1.59568 8.5 1.59375ZM8.23438 4.78125C8.39198 4.78125 8.54605 4.82799 8.6771 4.91555C8.80814 5.00311 8.91028 5.12756 8.97059 5.27317C9.03091 5.41878 9.04669 5.57901 9.01594 5.73359C8.98519 5.88817 8.9093 6.03016 8.79785 6.1416C8.68641 6.25305 8.54442 6.32894 8.38984 6.35969C8.23526 6.39044 8.07504 6.37466 7.92943 6.31434C7.78382 6.25403 7.65936 6.15189 7.5718 6.02084C7.48424 5.8898 7.4375 5.73573 7.4375 5.57812C7.4375 5.36678 7.52146 5.16409 7.6709 5.01465C7.82035 4.86521 8.02303 4.78125 8.23438 4.78125ZM9.03125 12.2188C8.74946 12.2188 8.47921 12.1068 8.27995 11.9076C8.08069 11.7083 7.96875 11.438 7.96875 11.1562V8.5C7.82786 8.5 7.69273 8.44403 7.5931 8.3444C7.49347 8.24477 7.4375 8.10965 7.4375 7.96875C7.4375 7.82785 7.49347 7.69273 7.5931 7.5931C7.69273 7.49347 7.82786 7.4375 7.96875 7.4375C8.25055 7.4375 8.5208 7.54944 8.72005 7.7487C8.91931 7.94796 9.03125 8.21821 9.03125 8.5V11.1562C9.17215 11.1562 9.30728 11.2122 9.4069 11.3118C9.50653 11.4115 9.5625 11.5466 9.5625 11.6875C9.5625 11.8284 9.50653 11.9635 9.4069 12.0632C9.30728 12.1628 9.17215 12.2188 9.03125 12.2188Z"
                    fill="#666666"
                  />
                </svg>
              </TooltipTrigger>
              <TooltipContent className="bg-black-900 flex-col text-white-A700 flex p-3 gap-2.5 items-center border-none rounded-lg">
                <div className=" border-b pb-2 border-[#666666]">
                  {row.getValue("companyName")}
                </div>
                {row.original.assignedTo}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      );
    },
    filterFn: "includesString",
    enableSorting: false,
  },
  {
    id: "name",
    accessorKey: "name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Name" />
    ),
    cell: ({ row }) => (
      <div className="flex gap-1.5 w-[150px] flex-col capitalize">
        {row.original.name}
        <p className="font-normal text-sm text-[#666666]">
          {row.original?.contactNumber}
        </p>
      </div>
    ),
    enableSorting: false,
  },
  {
    id: "entityName",
    accessorKey: "entityName",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Entity Name" />
    ),
    cell: ({ row }) => {
      return (
        <div className=" flex gap-10 capitalize items-center justify-between">
          {row.original.name}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <svg
                  width="17"
                  height="17"
                  viewBox="0 0 17 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.5 1.59375C7.13408 1.59375 5.79882 1.99879 4.6631 2.75766C3.52737 3.51653 2.64218 4.59514 2.11946 5.85709C1.59674 7.11904 1.45998 8.50766 1.72645 9.84734C1.99293 11.187 2.65069 12.4176 3.61655 13.3835C4.5824 14.3493 5.81298 15.0071 7.15266 15.2735C8.49234 15.54 9.88096 15.4033 11.1429 14.8805C12.4049 14.3578 13.4835 13.4726 14.2423 12.3369C15.0012 11.2012 15.4063 9.86593 15.4063 8.5C15.4043 6.66894 14.6761 4.91343 13.3813 3.61868C12.0866 2.32393 10.3311 1.59568 8.5 1.59375ZM8.23438 4.78125C8.39198 4.78125 8.54605 4.82799 8.6771 4.91555C8.80814 5.00311 8.91028 5.12756 8.97059 5.27317C9.03091 5.41878 9.04669 5.57901 9.01594 5.73359C8.98519 5.88817 8.9093 6.03016 8.79785 6.1416C8.68641 6.25305 8.54442 6.32894 8.38984 6.35969C8.23526 6.39044 8.07504 6.37466 7.92943 6.31434C7.78382 6.25403 7.65936 6.15189 7.5718 6.02084C7.48424 5.8898 7.4375 5.73573 7.4375 5.57812C7.4375 5.36678 7.52146 5.16409 7.6709 5.01465C7.82035 4.86521 8.02303 4.78125 8.23438 4.78125ZM9.03125 12.2188C8.74946 12.2188 8.47921 12.1068 8.27995 11.9076C8.08069 11.7083 7.96875 11.438 7.96875 11.1562V8.5C7.82786 8.5 7.69273 8.44403 7.5931 8.3444C7.49347 8.24477 7.4375 8.10965 7.4375 7.96875C7.4375 7.82785 7.49347 7.69273 7.5931 7.5931C7.69273 7.49347 7.82786 7.4375 7.96875 7.4375C8.25055 7.4375 8.5208 7.54944 8.72005 7.7487C8.91931 7.94796 9.03125 8.21821 9.03125 8.5V11.1562C9.17215 11.1562 9.30728 11.2122 9.4069 11.3118C9.50653 11.4115 9.5625 11.5466 9.5625 11.6875C9.5625 11.8284 9.50653 11.9635 9.4069 12.0632C9.30728 12.1628 9.17215 12.2188 9.03125 12.2188Z"
                    fill="#666666"
                  />
                </svg>
              </TooltipTrigger>
              <TooltipContent className="bg-black-900 flex-col text-white-A700 flex p-3 gap-2.5 items-center border-none rounded-lg">
                <div className=" border-b pb-2 border-[#666666]">
                  {row.original.name}
                </div>
                {row.original.assignedTo}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      );
    },
    enableSorting: false,
  },
  {
    id: "product",
    accessorKey: "product",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Product" />
    ),
    filterFn: (row, id, value) => {
      return String(row.getValue(id))
        .toLowerCase()
        .includes(String(value).toLowerCase());
    },
    cell: ({ row }) => {
      return (
        <div className="flex flex-col gap-1.5">
          <p className="capitalize whitespace-nowrap">
            {row.getValue("product")}
          </p>
          <p className="capitalize font-normal text-sm text-[#666666]">
            {row?.original?.subProduct}
          </p>
        </div>
      );
    },
    enableSorting: false,
  },
  {
    id: "assignedTo",
    accessorKey: "assignedTo",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        className="whitespace-nowrap"
        title="Assigned To"
      />
    ),
    cell: ({ row }) => {
      return (
        <div className="flex flex-col gap-1.5">
          <p className="capitalize">
            {row.getValue("assignedTo") || localStorage.getItem("name")}
          </p>
          <p className="capitalize font-normal text-sm text-[#666666]">
            {row?.original?.assignedToCity}
          </p>
        </div>
      );
    },
    filterFn: (row, id, value) => {
      return String(row.getValue(id))
        .toLowerCase()
        .includes(String(value).toLowerCase());
    },
    enableSorting: false,
  },
  {
    id: "channel",
    accessorKey: "channel",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Channel" />
    ),
    cell: ({ row }) => {
      const newSource = (channel_options as ChannelOptions)?.[
        (row?.original?.channelType as string)?.toLowerCase()
      ]?.find(
        (item) =>
          item.value?.toLowerCase() ===
          (row.getValue("channel") as string)?.toLowerCase().split(" ").join("")
      );

      if (row.getValue("channel") === "Website") {
        return <p className="capitalize">Website</p>;
      }

      return newSource?.value ? (
        <div className="grid grid-cols-1 max-w-[120px] place-content-center gap-1">
          <div className="flex items-start flex-col gap-3 justify-between w-full">
            <div className="h-full w-full gap-5 flex justify-between items-center">
              <p className="capitalize w-full whitespace-nowrap">
                {newSource?.label}
              </p>
              <Img
                className="text-right h-5 w-5"
                src={newSource?.icon}
                alt="icon"
              />
            </div>
            <p className="capitalize font-normal text-sm text-[#666666]">
              {["outletID", "partnerOffice", "contact"].includes(
                row?.original?.channelSource as string
              )
                ? row?.original?.channelSource === "partnerOffice"
                  ? "Partner"
                  : row?.original?.channelSource === "contact"
                  ? "Contact"
                  : row?.original?.channel === "Walk-in"
                  ? ""
                  : "Employee"
                : row?.original?.channelSource === "linkdin"
                ? "LinkedIn"
                : row?.original?.channelSource}
            </p>
          </div>
        </div>
      ) : (
        <p className="capitalize">{row?.original?.channelType}</p>
      );
    },
    enableSorting: false,
  },
  {
    accessorKey: "createDate",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Create Date " />
    ),
    filterFn: (row, id, value) => {
      return String(row.getValue(id))
        .toLowerCase()
        .includes(String(value).toLowerCase());
    },
  },
  {
    accessorKey: "opportunityStatus",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => {
      const values = close_reason_options.find(
        (item) => item.value === (row.getValue("opportunityStatus") as string)
      );
      if (!values) {
        return (
          <p
            style={{ color: "#23AB28", backgroundColor: "#E1FFE2" }}
            className="py-[3px] px-2 text-center rounded-md"
          >
            Open
          </p>
        );
      }
      return (
        <p
          style={{ color: values?.textColor, backgroundColor: values?.bgColor }}
          className="py-[3px] px-2 text-center rounded-md"
        >
          {values?.label}
        </p>
      );
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
    enableSorting: false,
  },
];

export default function OpportunitiesOverview() {
  const [opportunities, setOpportunities] = useState<OpportunityType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isRowDetailsOpen, setIsRowDetailsOpen] = useState<boolean>(false);
  const [selectedRowData, setSelectedRowData] = useState<OpportunityType>();
  const [activeTab, setTab] = useState("retail");
  const [assignedType, setAssignedType] = useState("assignedToMe");
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [productFilter, setProductFilter] = useState<string[]>([]);
  const [channelFilter, setChannelFilter] = useState<string[]>([]);
  const [search, setSearch] = useState<string>("");

  const handleFilterChange = (filters: Record<string, string[]>) => {
    // Update your existing state based on the filters
    setStatusFilter(filters.status || []);
    setProductFilter(filters.product || []);
    setChannelFilter(filters.channel || []);
  };

  const statusValue = (status: string) => {
    return status === "true" ? "open" : "close";
  };

  const handleRowClick = (row: OpportunityType) => {
    // if (pipelineType === "corporate") {
    //   navigate(`/leads/contact/${row.companyId}`);
    //   return;
    // }
    // navigate(`/leads/contact/${row.contactId}`);
    setIsRowDetailsOpen(true);
    setSelectedRowData(row);
  };

  const columns = useMemo(() => {
    return allcolumns.filter((column) => {
      // Add conditions to hide specific columns based on pipelineType
      if (activeTab === "retail") {
        return !["companyName", "entityName"].includes(column?.id as string);
      }
      if (activeTab === "corporate") {
        return !["name", "entityName"].includes(column?.id as string);
      }
      if (activeTab === "partner") {
        return !["companyName", "name"].includes(column?.id as string);
      }
      return true;
    });
  }, [activeTab]);

  const fetchopportunity = async () => {
    setLoading(true);
    const auth = localStorage.getItem("authToken");
    if (auth === null) return;
    const authToken = JSON.parse(auth);
    const payload: AxiosRequestConfig<GetFilteredLeadsReqType> = {
      headers: { Authorization: `Bearer ${authToken}` },
      params: {
        pipeline: activeTab === "corporate" ? "coorporate" : activeTab,
        type: assignedType,
        product_id: productFilter,
        channel_of_interaction_id: channelFilter,
        isOpen: statusFilter.map((status) =>
          status === "open" ? "true" : status === "close" ? "false" : status
        ),
        searchQuery: search,
      },
    };
    try {
      const res = (await getFilteredLeads(payload)) as any;
      setOpportunities(() =>
        res?.data?.map((item: any) => {
          const contact = item?._contacts?.length > 0 ? item?._contacts[0] : null;
          const customer = item?._customers;
          return {
            id: item?.id,
            uniqueId: item?.unique_id,
            // companyName: item?._coorporate_company?.[0]?.company_name,
            product: item?._product?.name,
            // assignedTo: item?._staff?.fname,
            createDate: getRelativeDate(item.created_at),
            opportunityStatus: item?.reasonToClose,
            channel: item?._channel_of_interaction?.[0]?.Channel,
            // companyId: item?._coorporate_company?.[0]?.id,
            // entityName: item?._partner?.[0]?.entityName,
            contactPerson: item?._contacts?.[0]?.contactName,
            // contactEmail: item?._contacts?.[0]?.contactEmail,
            // contactNumber: item?._contacts?.[0]?.contactNumber,
            // location: item?._contacts?.[0]?.contactAddress?.[0],
            // contactId: item?.contacts_id,
            name: contact?.contactName || customer?.full_name || "N/A",
            contactNumber: contact?.contactNumber || customer?.phone_number || "N/A",
            subProduct: item?._product?.subCategory,
            channelType: item?._channel_of_interaction?.[0]?.Type,
            channelSource: item?._channel_of_interaction?.[0]?.source,
            assignedTo: item?._assignedTo?.fname,
            assignedToCity: item?._assignedTo?.city,
            // assignedToCity: item?._assignedTo?.city,
            stage_tag: item?.stage_tags,
          };
        })
      );
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error("Failed to fetch Opportunities");
    }
  };
  useEffect(() => {
    fetchopportunity();
  }, [activeTab, assignedType, statusFilter, productFilter, channelFilter]);

  useDebounce(
    () => {
      fetchopportunity();
    },
    500,
    [search]
  );
  return (
    <>
      {loading && <Spinner />}
      <div className="flex h-full -m-4 gap-8 py-6 px-3 sm:px-0 w-full flex-col overflow-hidden">
        <div className=" flex w-full justify-between items-center">
          {/* <div className=" flex flex-col gap-2">
            <h3 className=" font-semibold leading-4 text-base text-black-label">
              <span className="capitalize">{pipelineType}</span> leads
            </h3>
            <p className="font-normal leading-[22.4px] text-sm text-[#7E7E7E]">
              List of all the recent {pipelineType} leads.
            </p>
          </div> */}
          <div className="w-full flex flex-col gap-8">
            <div className="flex justify-between w-full">
              <Tabs
                className="bg-navbar-menu-bg w-fit flex rounded-lg gap-6"
                value={activeTab}
                onValueChange={(val) => {
                  setTab(val);
                  // if (val === "all") {
                  //   fetchSearchResults();
                  // } else {
                  //   fetchCustomerContacts();
                  // }
                }}
              >
                <TabsList className="h-[44px]">
                  {tabData
                    .filter((tab) => true)
                    .map((tab, index: number) => (
                      <TabsTrigger
                        key={index}
                        value={tab.value}
                        disabled={!tab.enabled}
                        className={`py-3 px-10 disabled:opacity-40 text-sm h-[44px] max-w-[125px] font-semibold ${
                          tab.value === activeTab
                            ? "border border-blue-700 bg-white-A700 text-blue-700 rounded-lg"
                            : " text-black-label"
                        }`}
                      >
                        {tab.label}
                      </TabsTrigger>
                    ))}
                </TabsList>
              </Tabs>
              {/* <Link className="sm:hidden" to={"#"}>
                <button disabled className="rounded-lg disabled:opacity-40 py-[11px] px-4 border bg-blue-700 text-white-A700 font-semibold text-sm">
                  + New Lead
                </button>
              </Link> */}
            </div>
            <div className="flex justify-between sm:flex-col sm:p-2 sm:gap-5 items-start w-full">
              <Tabs
                className="flex rounded-lg gap-2"
                value={assignedType}
                onValueChange={(tab) => {
                  setAssignedType(tab);
                }}
              >
                <TabsList className=" sm:max-h-28 sm:h-full flex justify-start items-center gap-3 sm:flex-wrap">
                  {assignedTypeOptions.map(
                    (tab: { label: string; value: string }) => (
                      <TabsTrigger
                        key={tab.value}
                        value={tab.value}
                        className={` py-1.5 px-[18px] text-sm h-[32px] font-normal rounded-lg ${
                          tab.value === assignedType
                            ? " bg-[#E1EBFC] text-blue-700"
                            : " text-black-label bg-none"
                        }`}
                      >
                        {tab.label}
                      </TabsTrigger>
                    )
                  )}
                </TabsList>
              </Tabs>          
              <GenericFilter
                config={opportunitiesFilterConfig}
                onFilterChange={handleFilterChange}
                className="flex gap-5 items-center"
                filterTriggerClassName="" // Add your existing filter trigger styles
                activeFilterClassName="" // Add your existing active filter pill styles
              />
            </div>
          </div>
        </div>
        <div className="flex-1 relative overflow-auto">
          {false && <Spinner />}
          <DataTable
            columns={columns}
            data={opportunities}
            isToolbarVisible={false}
            onRowClick={handleRowClick}
            highlightRow={isRowDetailsOpen}
            isPaginationVisible={opportunities?.length > 10}
            searchData={searchOptions[activeTab as keyof typeof searchOptions]}
            isPageCountVisible={false}
            defaultSorting={[{ id: "id", desc: true }]}
          />
          {isRowDetailsOpen && (
            <RowDetailsSheet
              isOpen={isRowDetailsOpen}
              onOpenChange={setIsRowDetailsOpen}
              rowData={selectedRowData as any}
              getOpportunityList={fetchopportunity}
            />
          )}
        </div>
        <Toaster position="top-right" />
      </div>
    </>
  );
}
